/**
 * The one definition of an organization's profile strength.
 *
 * Every surface that talks about how complete a profile is — the admin
 * completeness card, the editor's readiness card, the post-login destination,
 * and (later) the Resend lifecycle events — derives it here, so they can never
 * disagree about whether a profile is ready.
 *
 * Scoring is one point per field, so a section's weight is its field count.
 * That is deliberate and tunable, not an accident.
 *
 * Reads follow the precedence EditProfilePage already uses: the dedicated
 * column first, the profile_data JSONB key as a fallback. Where the wizard and
 * the editor disagree, the column wins.
 *
 * `provides_leads` and `partnership_opportunities` are deliberately NOT scored.
 * Both are booleans whose `false` is indistinguishable from unset, so scoring
 * them would penalize an organization for honestly offering no lead program.
 * Their narrative counterparts carry the signal instead.
 *
 * `orgProfileStrength.test.ts` pins all of the above. Change this module first,
 * watch the test fail, then change the callers.
 */

export type OrgStrengthTier = "draft" | "ready" | "complete" | "standout";

/** The profile editor's tabs, so a missing field can be jumped to. */
export type EditorTab = "branding" | "value" | "development" | "marketData" | "agentResults";

export interface MissingItem {
  /** Stable field key — what `profile_data.skippedFields` stores. Never a label. */
  key: string;
  label: string;
  tab: EditorTab;
  sectionId: string;
  /** True when the organization may mark this field "doesn't apply". */
  skippable: boolean;
}

export interface OrgStrengthSection {
  key: string;
  label: string;
  /** Fields with real content. */
  filled: number;
  /**
   * Fields answered without content: skippable fields the organization marked
   * "doesn't apply", and fields in a group it does not offer (`confirmed_none`).
   */
  skipped: number;
  total: number;
  /** Labels of fields neither filled nor skipped. */
  missing: string[];
  /** The same entries as `missing`, with key, skippable and navigation metadata. */
  missingItems: MissingItem[];
}

export type PublishChecklistKey = "identity" | "story" | "offerOrGrowth";

export interface PublishChecklistItem {
  key: PublishChecklistKey;
  label: string;
  /** Filled + skipped fields counted toward this item. */
  count: number;
  required: number;
  done: boolean;
  /** What could close the gap (empty when done). */
  missingItems: MissingItem[];
}

export interface OrgProfileStrength {
  /** 0–100, rounded. Filled + skipped over all fields. */
  pct: number;
  tier: OrgStrengthTier;
  sections: OrgStrengthSection[];
  /** Every field label neither filled nor skipped, in section order. */
  missing: string[];
  /**
   * The same fields as `missing`, carrying navigation metadata, in section
   * order. Surfaces that LIST what is left must read this rather than
   * re-flattening `sections` themselves — the editor's readiness card did
   * that and then showed only its first four entries, so an admin was told
   * "11 of 23" beside a list naming four (founder audit, 2026-09-17).
   */
  missingItems: MissingItem[];
  /**
   * The missing fields that block publishing: the union of the checklist's
   * own items, deduplicated, in section order.
   */
  requiredMissing: MissingItem[];
  /**
   * Every other missing field — real improvements, but nothing publishing
   * waits on. `requiredMissing.length + optionalMissing.length ===
   * missingItems.length`, always.
   */
  optionalMissing: MissingItem[];
  /**
   * The counts every surface must quote, derived once so they cannot drift.
   * `filled + skipped + missing === total`, and `total` is
   * ORG_STRENGTH_FIELD_COUNT. `pct` is `(filled + skipped) / total`.
   */
  totals: { filled: number; skipped: number; missing: number; total: number };
  /** Label of the section with the most fields neither filled nor skipped, or null when complete. */
  topMissingSection: string | null;
  /** The three requirements "ready to publish" is made of, in display order. */
  publishChecklist: PublishChecklistItem[];
}

/** A loose org row — callers pass the Supabase row straight through. */
type OrgRow = Record<string, unknown> | null | undefined;

const hasText = (v: unknown): boolean => typeof v === "string" && v.trim() !== "";

/**
 * True when the featured quote carries real text.
 *
 * `profile_data.featuredQuote` is stored as `{ text, attribution }` — the
 * editor seeds `{ text: "", attribution: "" }` (orgProfileEditorData) and
 * FeaturedQuoteSection writes that object back. A bare `hasText` therefore
 * never counted it, so no organization could ever score the field. A quote is
 * filled when its `text` is non-blank; a blank text with an attribution is
 * still empty. A legacy plain string is accepted for rows written before the
 * object shape existed.
 */
const hasQuoteText = (v: unknown): boolean => {
  if (typeof v === "string") return hasText(v);
  if (v && typeof v === "object") return hasText((v as { text?: unknown }).text);
  return false;
};

/**
 * True when a list holds at least one real entry.
 *
 * Blank strings do not count, and that is load-bearing rather than tidiness.
 * The profile editor seeds `locations: [""]` so the UI renders one empty input
 * (EditProfilePage's load effect), and its save path filters those blanks back
 * out before writing `office_locations`. A bare `length > 0` therefore scored
 * the editor's placeholder as a filled Office locations field while the saved
 * org row scored it as unfilled — one field, ~4 points, and the editor and the
 * onboarding handoff disagreed about the same organization. Non-string entries
 * (the object lists: trainingPrograms, technologyTools) are counted as before.
 */
const hasItems = (v: unknown): boolean =>
  Array.isArray(v) && v.some((x) => (typeof x === "string" ? x.trim() !== "" : x != null));

/** The groups of `private.office_profile_groups()` that hold a scored field. */
export type OfficeProfileGroupName =
  | "brand" | "compensation" | "training" | "benefits" | "technology" | "partnerships";

interface FieldDef {
  /** Stable key, stored in profile_data.skippedFields. Never rename. */
  key: string;
  label: string;
  /**
   * Where the editor renders this field, so the widget can jump to it. Tabs and
   * section ids are the real ones passed to ProfileEditorCards in
   * EditProfilePage — see the `sectionIds` arrays there.
   */
  tab: EditorTab;
  sectionId: string;
  /** Only these may be skipped. See SKIPPABLE_FIELD_KEYS. */
  skippable?: boolean;
  /**
   * The office-profile group (spec 8.1, `private.office_profile_groups()`)
   * holding every value `isFilled` reads, or none when the field is not in a
   * group. A group in the row's `confirmed_none` counts the field as answered.
   */
  group?: OfficeProfileGroupName;
  /** True when the organization has supplied this field. */
  isFilled: (
    org: Record<string, unknown>,
    pd: Record<string, unknown>,
    memberCount: number,
  ) => boolean;
}

interface SectionDef {
  key: string;
  label: string;
  fields: FieldDef[];
}

const SECTIONS: SectionDef[] = [
  {
    key: "identity",
    label: "Identity",
    fields: [
      { key: "logo", label: "Logo", tab: "branding", sectionId: "brand", group: "brand", isFilled: (o, pd) => hasText(o.logo_url) || hasText(pd.logo) },
      { key: "tagline", label: "Tagline", tab: "branding", sectionId: "brand", isFilled: (_o, pd) => hasText(pd.tagline) },
      { key: "description", label: "Description", tab: "branding", sectionId: "brand", isFilled: (o, pd) => hasText(o.description) || hasText(pd.description) },
      { key: "websiteUrl", label: "Website", tab: "branding", sectionId: "brand", isFilled: (o, pd) => hasText(o.website) || hasText(pd.websiteUrl) },
      // Read the office_locations mirror only, NOT profile_data.locations: the
      // mirror is the authoritative source (trigger-maintained from org_offices),
      // and a stale pd.locations left over from before an office was deleted must
      // not keep scoring a removed office. The live editor maps its form
      // locations into office_locations via profileDataToOrgRow, so it is
      // unaffected.
      { key: "locations", label: "Office locations", tab: "branding", sectionId: "brand", isFilled: (o) => hasItems(o.office_locations) },
    ],
  },
  {
    key: "story",
    label: "About us",
    fields: [
      { key: "missionStatement", label: "Mission statement", tab: "branding", sectionId: "about", group: "brand", isFilled: (_o, pd) => hasText(pd.missionStatement) },
      { key: "whatMakesUsDifferent", label: "What makes us different", tab: "branding", sectionId: "about", group: "brand", isFilled: (_o, pd) => hasText(pd.whatMakesUsDifferent) },
      { key: "ourStory", label: "Our story", tab: "branding", sectionId: "about", isFilled: (_o, pd) => hasText(pd.ourStory) },
      { key: "featuredQuote", label: "Featured quote", tab: "branding", sectionId: "featuredQuote", skippable: true, isFilled: (_o, pd) => hasQuoteText(pd.featuredQuote) },
    ],
  },
  {
    key: "offer",
    label: "The offer",
    fields: [
      { key: "compensationModels", label: "Compensation models", tab: "value", sectionId: "compensation", group: "compensation", isFilled: (o, pd) => hasItems(o.compensation_models) || hasItems(pd.compensationModels) },
      { key: "selectedBenefits", label: "Benefits", tab: "value", sectionId: "benefits", group: "benefits", isFilled: (o, pd) => hasItems(o.selected_benefits) || hasItems(pd.selectedBenefits) },
      { key: "compensationPhilosophy", label: "Compensation philosophy", tab: "value", sectionId: "compensation", group: "compensation", isFilled: (_o, pd) => hasText(pd.compensationPhilosophy) },
      { key: "feeNotes", label: "Fee notes", tab: "value", sectionId: "compensation", group: "compensation", skippable: true, isFilled: (_o, pd) => hasText(pd.feeNotes) },
    ],
  },
  {
    key: "growth",
    label: "Growth",
    fields: [
      { key: "trainingPrograms", label: "Training programs", tab: "development", sectionId: "training", group: "training", skippable: true, isFilled: (_o, pd) => hasItems(pd.trainingPrograms) },
      { key: "trainingFrequency", label: "Training cadence", tab: "development", sectionId: "training", group: "training", isFilled: (o, pd) => hasItems(o.training_frequency) || hasItems(pd.trainingFrequency) },
      { key: "careerPathDescription", label: "Career path", tab: "development", sectionId: "growth", group: "training", skippable: true, isFilled: (_o, pd) => hasText(pd.careerPathDescription) },
      { key: "partnershipDetails", label: "Partnership details", tab: "development", sectionId: "growth", group: "partnerships", skippable: true, isFilled: (_o, pd) => hasText(pd.partnershipDetails) },
    ],
  },
  {
    key: "operations",
    label: "Operations",
    fields: [
      { key: "technologyTools", label: "Technology", tab: "branding", sectionId: "technologyTools", group: "technology", isFilled: (_o, pd) => hasItems(pd.techStack) || hasItems(pd.technologyTools) },
      { key: "marketingSupport", label: "Marketing support", tab: "value", sectionId: "support", group: "benefits", skippable: true, isFilled: (_o, pd) => hasItems(pd.marketingSupport) },
      { key: "adminAssistance", label: "Admin assistance", tab: "value", sectionId: "support", group: "benefits", skippable: true, isFilled: (_o, pd) => hasText(pd.adminAssistance) },
    ],
  },
  {
    key: "reach",
    label: "Reach",
    fields: [
      { key: "serviceTags", label: "Service types", tab: "branding", sectionId: "brand", isFilled: (o, pd) => hasItems(o.service_tags) || hasItems(pd.serviceTags) },
      { key: "marketsServed", label: "Markets served", tab: "marketData", sectionId: "performanceMarketData", skippable: true, isFilled: (_o, pd) => hasItems(pd.marketsServed) },
    ],
  },
  {
    key: "team",
    label: "Team",
    fields: [
      // The member count is not on the org row, so every caller must supply it —
      // including the editor's widget, which used to hardcode 0 and therefore
      // under-reported this section against the feed card and the handoff. See
      // useOrgMemberCount, the one query all three read it from.
      { key: "teamMembers", label: "Team members invited", tab: "branding", sectionId: "brand", isFilled: (_o, _pd, memberCount) => memberCount > 1 },
    ],
  },
];

/**
 * The scored field labels in each editor section, in field order.
 *
 * A card's summary must name what it counted rather than claim the card is
 * "Complete". The Market Data card is the case that forced this: it renders
 * about ten production metrics and scores exactly one of them, Markets
 * served, so an admin who filled that one was told "Market Data · Complete"
 * beside ten empty numbers, while Preview correctly showed no production data
 * at all (founder audit, 2026-09-17).
 *
 * Derived from the field definitions, never hand-listed, so a field that moves
 * section moves here with it.
 */
export const SCORED_LABELS_BY_SECTION: Readonly<Record<string, readonly string[]>> = Object.freeze(
  SECTIONS.reduce<Record<string, string[]>>((acc, section) => {
    for (const field of section.fields) (acc[field.sectionId] ??= []).push(field.label);
    return acc;
  }, {}),
);

/** Total scored fields. Exported so tests assert against one definition. */
export const ORG_STRENGTH_FIELD_COUNT = SECTIONS.reduce((n, s) => n + s.fields.length, 0);

const ALL_FIELDS = SECTIONS.flatMap((s) => s.fields);

/**
 * The fields an organization may mark "doesn't apply". Approved 2026-09-15:
 * things not every firm has. Identity, compensation models, benefits and the
 * other core fields are deliberately absent.
 */
export const SKIPPABLE_FIELD_KEYS: readonly string[] = Object.freeze(
  ALL_FIELDS.filter((f) => f.skippable).map((f) => f.key),
);

const asRow = (org: OrgRow) => (org ?? {}) as Record<string, unknown>;
const pdOf = (row: Record<string, unknown>) => (row.profile_data ?? {}) as Record<string, unknown>;

/** The valid skips in a document: strings naming a skippable field. Anything else is ignored. */
const readSkips = (pd: Record<string, unknown>): Set<string> => {
  const raw = pd.skippedFields;
  if (!Array.isArray(raw)) return new Set();
  return new Set(raw.filter((k): k is string => typeof k === "string" && SKIPPABLE_FIELD_KEYS.includes(k)));
};

/**
 * The groups an office said it does not offer ("Not offered here", spec 8.3):
 * `confirmed_none` on the row, as `org_effective_profiles` carries it. Anything
 * other than an array of strings is ignored, so an organization's own row
 * (which has no such column) and the editor's draft score as before.
 */
const readConfirmedNone = (row: Record<string, unknown>): Set<string> => {
  const raw = row.confirmed_none;
  if (!Array.isArray(raw)) return new Set();
  return new Set(raw.filter((g): g is string => typeof g === "string"));
};

/** The scored field keys in each group, derived from the field definitions. */
export const STRENGTH_FIELDS_BY_GROUP: Readonly<Record<string, readonly string[]>> = Object.freeze(
  ALL_FIELDS.reduce<Record<string, string[]>>((acc, f) => {
    if (f.group) (acc[f.group] ??= []).push(f.key);
    return acc;
  }, {}),
);

/** True when the field named by `key` has real content. Unknown keys are never filled. */
export const isFieldFilled = (key: string, org: OrgRow, memberCount = 0): boolean => {
  const def = ALL_FIELDS.find((f) => f.key === key);
  if (!def) return false;
  const row = asRow(org);
  return def.isFilled(row, pdOf(row), memberCount);
};

/**
 * `skips` with `key` added (skipped = true) or removed. Never duplicates.
 *
 * Does not validate `key`; invalid keys are ignored when read (readSkips) and
 * dropped by pruneFilledSkips.
 */
export const setSkipped = (skips: readonly string[] | undefined, key: string, skipped: boolean): string[] => {
  const rest = (skips ?? []).filter((k) => k !== key);
  return skipped ? [...rest, key] : rest;
};

/**
 * The document's skips minus any whose field now has content, and minus
 * invalid keys. Filled beats skipped; the editor saves this so a skip left
 * behind by content arriving elsewhere (wizard, import, Console) is cleaned up.
 */
export const pruneFilledSkips = (org: OrgRow, memberCount = 0): string[] => {
  const row = asRow(org);
  const pd = pdOf(row);
  return [...readSkips(pd)].filter((key) => !isFieldFilled(key, row, memberCount));
};

/**
 * The publish threshold, as a minimum counted (filled or skipped) field count
 * per section.
 *
 * These are calibrated against real data, not chosen by feel. Measured on
 * 2026-08-12, requiring FULLY filled sections put **0 of 14** real
 * organizations over the line — including the one this threshold exists to
 * catch, a profile with 13 of 15 sections filled that had never been published.
 * A complete four-field section demands feeNotes AND partnershipDetails AND
 * careerPathDescription, which many brokerages have nothing honest to say about.
 *
 * At these numbers three organizations clear the bar and exactly one of them is
 * unpublished — the org the publish nudge should reach.
 *
 * Tuning these changes three behaviours at once: whether the editor's
 * readiness card renders, where a returning admin lands after signing in, and
 * whether the Org-Ready-Unpublished lifecycle email fires. Re-measure before
 * moving them.
 *
 * The 2026-08-12 calibration predates skips; a skip can only move an
 * organization over the line, never below it.
 */
export const READY_MIN_IDENTITY = 4;
export const READY_MIN_STORY = 3;
/** Applied to The offer and to Growth; clearing either one is enough. */
export const READY_MIN_OFFER_OR_GROWTH = 2;

export const computeOrgProfileStrength = (org: OrgRow, memberCount = 0): OrgProfileStrength => {
  const row = asRow(org);
  const pd = pdOf(row);
  const skips = readSkips(pd);
  const none = readConfirmedNone(row);

  const sections: OrgStrengthSection[] = SECTIONS.map((def) => {
    let filled = 0;
    let skipped = 0;
    const uncounted: FieldDef[] = [];
    for (const f of def.fields) {
      if (f.isFilled(row, pd, memberCount)) filled += 1;
      // A skipped field, or a field in a group the office does not offer
      // (D8, #945): both are answered. Unknown is not in confirmed_none, so it
      // stays missing.
      else if ((f.skippable && skips.has(f.key)) || (f.group !== undefined && none.has(f.group))) skipped += 1;
      else uncounted.push(f);
    }
    return {
      key: def.key,
      label: def.label,
      filled,
      skipped,
      total: def.fields.length,
      missing: uncounted.map((f) => f.label),
      missingItems: uncounted.map((f) => ({
        key: f.key,
        label: f.label,
        tab: f.tab,
        sectionId: f.sectionId,
        skippable: !!f.skippable,
      })),
    };
  });

  const byKey = (key: string) => sections.find((s) => s.key === key)!;
  const counted = (key: string) => byKey(key).filled + byKey(key).skipped;

  const totalCounted = sections.reduce((n, s) => n + s.filled + s.skipped, 0);
  const pct = Math.round((totalCounted / ORG_STRENGTH_FIELD_COUNT) * 100);

  // The stronger of The offer and Growth carries the third item; offer wins a tie.
  const altKey = counted("growth") > counted("offer") ? "growth" : "offer";
  const checklistItem = (
    key: PublishChecklistKey,
    sectionKey: string,
    required: number,
    labelOverride?: string,
  ): PublishChecklistItem => {
    const count = counted(sectionKey);
    const done = count >= required;
    return {
      key,
      label: labelOverride ?? byKey(sectionKey).label,
      count,
      required,
      done,
      missingItems: done ? [] : [...byKey(sectionKey).missingItems],
    };
  };
  const publishChecklist: PublishChecklistItem[] = [
    checklistItem("identity", "identity", READY_MIN_IDENTITY),
    checklistItem("story", "story", READY_MIN_STORY),
    checklistItem("offerOrGrowth", altKey, READY_MIN_OFFER_OR_GROWTH, "The offer or Growth"),
  ];

  // "Ready to publish" is a strict prerequisite for every higher tier, so
  // `tier !== "draft"` means exactly "ready to publish" everywhere it is read.
  const ready = publishChecklist.every((i) => i.done);
  const everySectionTouched = sections.every((s) => s.filled + s.skipped > 0);

  // Each tier demands everything the one below it does, so a higher tier can
  // never be reached by a profile that would fail a lower one.
  const tier: OrgStrengthTier = !ready
    ? "draft"
    : !everySectionTouched || pct < 70
    ? "ready"
    : pct >= 90
    ? "standout"
    : "complete";

  const emptiest = sections
    .filter((s) => s.missing.length > 0)
    .sort((a, b) => b.missing.length - a.missing.length)[0];

  const missing = sections.flatMap((s) => s.missing);
  const missingItems = sections.flatMap((s) => s.missingItems);

  // A field can appear under more than one checklist item only if the section
  // definitions change, but dedupe by key anyway: a field listed twice would
  // break the reconciliation this object exists to guarantee.
  const requiredKeys = new Set(publishChecklist.flatMap((i) => i.missingItems.map((m) => m.key)));
  const requiredMissing = missingItems.filter((m) => requiredKeys.has(m.key));
  const optionalMissing = missingItems.filter((m) => !requiredKeys.has(m.key));

  const totalFilled = sections.reduce((n, s) => n + s.filled, 0);
  const totalSkipped = sections.reduce((n, s) => n + s.skipped, 0);

  return {
    pct,
    tier,
    sections,
    missing,
    missingItems,
    requiredMissing,
    optionalMissing,
    totals: {
      filled: totalFilled,
      skipped: totalSkipped,
      missing: missingItems.length,
      total: ORG_STRENGTH_FIELD_COUNT,
    },
    topMissingSection: emptiest ? emptiest.label : null,
    publishChecklist,
  };
};

/** True once the profile clears the publish threshold. */
export const isReadyToPublish = (s: OrgProfileStrength): boolean => s.tier !== "draft";

/**
 * The subset of the editor's profile data this adapter reads by name.
 *
 * Structural, and deliberately not an import of EditProfilePage's `ProfileData`:
 * this module sits below the pages layer and every other surface that scores a
 * profile passes a plain row. Every field is optional and `unknown` so both the
 * editor's fully-typed object and a bare `Record<string, unknown>` satisfy it —
 * the whole object is still forwarded as `profile_data`, where the field
 * definitions above read the keys this adapter does not name. That forwarding
 * is why the adapter is generic over its argument rather than taking this type
 * directly: callers legitimately pass objects carrying far more keys than these.
 */
export interface OrgProfileDataLike {
  logo?: unknown;
  description?: unknown;
  websiteUrl?: unknown;
  locations?: unknown;
  compensationModels?: unknown;
  selectedBenefits?: unknown;
  trainingFrequency?: unknown;
  serviceTags?: unknown;
  skippedFields?: unknown;
}

/**
 * Scores the editor's in-memory ProfileData by shaping it like an organizations
 * row. The editor's widget must reflect unsaved edits, so it cannot read the
 * database row the rest of the app scores — but it must use the same field
 * definitions, or the editor and the lifecycle emails will disagree.
 */
export const profileDataToOrgRow = <T extends OrgProfileDataLike>(
  pd: T,
): Record<string, unknown> => ({
  logo_url: pd.logo,
  description: pd.description,
  website: pd.websiteUrl,
  office_locations: pd.locations,
  compensation_models: pd.compensationModels,
  selected_benefits: pd.selectedBenefits,
  // The editor stores trainingFrequency as a bare string; the org row stores an
  // array. Normalize so one field definition can read both.
  training_frequency: Array.isArray(pd.trainingFrequency)
    ? pd.trainingFrequency
    : typeof pd.trainingFrequency === "string" && pd.trainingFrequency.trim() !== ""
    ? [pd.trainingFrequency]
    : [],
  service_tags: pd.serviceTags,
  profile_data: pd,
});
